/* eslint-disable react/jsx-no-target-blank */
import React, { useRef } from 'react';
import classNames from 'classnames';
import { useHover } from 'usehooks-ts';
import HotelCardGalleryOverlay from './components/HotelCardGalleryOverlay';
import HotelCardFeatures from './components/HotelCardFeatures';
import HotelCardPrice from './components/HotelCardPrice';
import HotelCardLabels from './components/HotelCardLabels';
import HotelCardHeaderLabel from './components/HotelCardHeaderLabel';
import HotelCardAdjacentTours from './components/HotelCardAdjacentTours';
import type { HotelCardProps } from './types';
import HotelCardPenalizedFilters from './components/HotelCardPenalizedFilters';
import { HOTEL_CARD_HOTEL_INFO, HOTEL_CARD_TEST_ID } from './constants';
import useLongHover from './hooks/useLongHover';
import styles from './hotelCardSmall.module.css';
import { sliceFeatures } from './components/HotelCardFeatures/utils';
import {
  MAX_FEATURES_VISIBLE_VALUE,
  MAX_FEATURES_VISIBLE_VALUE_WITH_LIFT,
} from './components/HotelCardFeatures/constants';

const HotelCardSmall: React.FC<HotelCardProps> = (props) => {
  const {
    id,
    images,
    bonusCount,
    currencyCode,
    hotelPageVisited,
    stars,
    hotelName,
    hotelLocation,
    rating,
    reviewsCount,
    features,
    instantConfirm,
    isFlexDates,
    price,
    innerLabels,
    headerLabel,
    isHotelOnly,
    availability,
    adjacentTours = [],
    datesInfo,
    penalizedFilters = [],
    hotelPageHref,
    openInCurrentTab,
    className,
    onLongHover,
    cashbackLabel,
    cashbackIconWebp,
    cashbackIconPng,
    cashbackTooltipText,
    labelColor,
    textColor,
    skiLiftDistance,
  } = props;

  const cardRef = useRef<HTMLDivElement>(null);
  const isCardHovered = useHover(cardRef);

  useLongHover(isCardHovered, onLongHover);

  const visibleFatures = skiLiftDistance
    ? sliceFeatures(features, MAX_FEATURES_VISIBLE_VALUE_WITH_LIFT)
    : sliceFeatures(features, MAX_FEATURES_VISIBLE_VALUE);

  return (
    <div
      ref={cardRef}
      data-testid={HOTEL_CARD_TEST_ID}
      className={classNames(styles.cardContainer, className)}
    >
      <HotelCardGalleryOverlay
        id={id}
        price={price}
        images={images}
        isSmall
        bonusCount={bonusCount}
        hotelPageVisited={hotelPageVisited}
        stars={stars}
        hotelName={hotelName}
        hotelLocation={hotelLocation}
        rating={rating}
        reviewsCount={reviewsCount}
        hotelPageHref={hotelPageHref}
        openInCurrentTab={openInCurrentTab}
        cashbackTooltipText={cashbackTooltipText}
        labelColor={labelColor}
        textColor={textColor}
        cashbackIconWebp={cashbackIconWebp}
        cashbackIconPng={cashbackIconPng}
        cashbackLabel={cashbackLabel}
      />
      <div
        className={classNames(styles.hotelInfo, {
          [styles.hotelInfoNoPaddingBottom]:
            !adjacentTours.length && !penalizedFilters.length,
        })}
      >
        <HotelCardHeaderLabel headerLabel={headerLabel} isMobile />
        <a
          data-testid={HOTEL_CARD_HOTEL_INFO}
          href={hotelPageHref}
          target={openInCurrentTab ? '_self' : '_blank'}
          className={styles.linkToHotel}
        >
          <HotelCardFeatures
            isMobile
            features={visibleFatures}
            skiLiftDistance={skiLiftDistance}
          />
        </a>
        <HotelCardLabels isMobile labels={innerLabels} />
        <a
          href={hotelPageHref}
          target={openInCurrentTab ? '_self' : '_blank'}
          className={styles.linkToHotel}
        >
          <HotelCardPrice
            availability={availability}
            instantConfirm={instantConfirm}
            isMobile
            isFlexDates={isFlexDates}
            isMapCard={false}
            price={price}
            currencyCode={currencyCode}
            isHotelOnly={isHotelOnly}
            datesInfo={datesInfo}
          />
        </a>
        <HotelCardAdjacentTours
          isFlexDates={isFlexDates}
          adjacentTours={adjacentTours}
          currencyCode={currencyCode}
          isHotelOnly={isHotelOnly}
          isMobile
        />
        {penalizedFilters.length ? (
          <a
            href={hotelPageHref}
            target={openInCurrentTab ? '_self' : '_blank'}
            className={styles.linkToHotel}
          >
            <HotelCardPenalizedFilters
              isMobile
              penalizedFilters={penalizedFilters}
            />
          </a>
        ) : null}
      </div>
    </div>
  );
};

export default HotelCardSmall;
